import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AttendanceContextProps, AttendanceMessage, AttendanceProviderProps, Group, MessageGroups, MessageStatus, OperatorUrgency, SpecificAttendanceDetails, SupervisorUrgency } from "../../interfaces/Attendances.interfaces";
import { CustomState } from "../../Hooks/State.hook";
import { SocketContext } from "../global/SocketContext";
import { ReactNode } from "react";
import { OficialWhatsappMessageTemplate } from "../../interfaces/MessageTemplates.interfaces";
import api from "../../services/api";
import { LoginContext, currentRoute } from "../global/LoginContext";
import { CustomerWithContact } from "../../interfaces/Customers.interface";
import { Modal } from "../../styles/modal";
import { NewAttendance } from "../../pages/Operador/OperadorHeader/NewAttendanceModal";
import formatarCPForCNPJ from "../../Hooks/FormatCPFCNPJ.hook";
import formatarNome from "../../Hooks/FormatName.hook";
import logo from "../../logoh.png";
import { decodeSafeURI } from "../../Hooks/decodeSafeUri.hook";

export const AttendancesContext = createContext({} as AttendanceContextProps);

export const AttendancesProvider = ({ children }: AttendanceProviderProps) => {
    const detailedAttendances = CustomState<Array<SpecificAttendanceDetails>>([]);
    const groups = CustomState<Array<Group>>([]);
    const attendancesMessages = CustomState<Array<AttendanceMessage>>([]);
    const attendancesMessagesGrouped = CustomState<MessageGroups>({});
    const readingAttendance = CustomState<SpecificAttendanceDetails | null>(null);
    const readingGroup = CustomState<Group | null>(null);
    const modalState = CustomState<ReactNode>("");
    const pausedState = CustomState<boolean>(false);
    const selectedTemplate = CustomState<OficialWhatsappMessageTemplate | null>(null);
    const selectedClient = CustomState<CustomerWithContact | null>(null);
    const loadedUser = CustomState<number | null>(null);
    const newAttendanceModalState = CustomState<boolean>(false);
    const [alreadySetLastMessages, setAlreadySetLastMessages] = useState(false);
    const { socket } = useContext(SocketContext);
    const { user } = useContext(LoginContext);

    useEffect(() => {

        if (detailedAttendances.value.length) {
            const sortedAttendance = detailedAttendances.value.map((at) => {
                const messages = attendancesMessagesGrouped.value?.[at.CODIGO_NUMERO]
                if (messages) {
                    at.ULTIMA_MENSAGEM = messages[0];
                }
                return at;
            }).sort((a, b) => {
                const aDateTime = a.ULTIMA_MENSAGEM?.DATA_HORA ? (new Date(a.ULTIMA_MENSAGEM.DATA_HORA)).getTime() : 0;
                const bDateTime = b.ULTIMA_MENSAGEM?.DATA_HORA ? (new Date(b.ULTIMA_MENSAGEM.DATA_HORA)).getTime() : 0;
                return ((aDateTime > bDateTime) ? -1 : ((aDateTime < bDateTime) ? 1 : 0))
            })

            if (detailedAttendances.value[0].CODIGO != sortedAttendance[0].CODIGO) {
                detailedAttendances.set(sortedAttendance);
            }
        }

    }, [detailedAttendances.value, attendancesMessagesGrouped.value]);

    useEffect(() => {
        if (user && loadedUser.value !== user.CODIGO) {
            loadedUser.set(user.CODIGO);

            if (currentRoute === "vollo" && user.SETOR === 15) {
                api.get<SpecificAttendanceDetails[]>(`/custom-routes/operator-attendances/-5000`)
                    .then(async (res) => {
                        await loadAllOperatorAttendancesMessages(-5000);

                        detailedAttendances.set(prev => [...prev, ...res.data]);
                    });

                return;
            }

            api.get<SpecificAttendanceDetails[]>(`/custom-routes/operator-attendances/${user.CODIGO}`)
                .then(async (res) => {
                    await loadAllOperatorAttendancesMessages(user.CODIGO);

                    detailedAttendances.set(prev => [...prev, ...res.data]);
                });

            if ((user.NIVEL === "ADMIN") || currentRoute === "ccr" && user.SETOR === 2) {
                api.get<SpecificAttendanceDetails[]>(`/custom-routes/operator-attendances/-1`)
                    .then(async (res) => {
                        await loadAllOperatorAttendancesMessages(-1);

                        detailedAttendances.set(prev => [...prev, ...res.data]);
                    });
                if ([6, 9].includes(user.CODIGO) && currentRoute === "odim") {
                    api.get<SpecificAttendanceDetails[]>(`/custom-routes/operator-attendances/${user.CODIGO === 6 ? 9 : 6}`)
                        .then(async (res) => {
                            await loadAllOperatorAttendancesMessages(9);

                            detailedAttendances.set(prev => [...prev, ...res.data]);
                        });
                }
            }

            if ((currentRoute === "infotec")) {
                api.get<SpecificAttendanceDetails[]>(`/custom-routes/operator-attendances/-2`)
                    .then(async (res) => {
                        await loadAllOperatorAttendancesMessages(-2);

                        detailedAttendances.set(prev => [...prev, ...res.data]);
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loadedUser.value]);


    async function loadAllOperatorAttendancesMessages(operatorId: number) {
        if (user) {
            const res = await api.get<Record<string, Array<AttendanceMessage>>>(`/messages/operator/${operatorId}`);

            attendancesMessagesGrouped.set((prev) => ({ ...prev, ...res.data }));
            setAlreadySetLastMessages(false);
        }
    }

    async function loadMessages(numberId: number) {
        const { data } = await api.get<Array<AttendanceMessage>>(`/messages/${numberId}`);

        if (currentRoute === "vollo" && user && user.SETOR === 15) {
            attendancesMessagesGrouped.set((prev) => {
                return { ...prev, [numberId]: data.filter(m => [-5000, 10].includes(m.CODIGO_OPERADOR)) }
            })
        } else {
            attendancesMessagesGrouped.set((prev) => {
                return { ...prev, [numberId]: data }
            })
        }
    };

    function finishAttendance(resultId: number, scheduleDate?: Date) {
        socket.value && user && socket.value.emit("finish-attendance", {
            clientName: currentRoute,
            operatorId: user.CODIGO,
            sectorId: user.SETOR,
            attendanceId: readingAttendance.value!.CODIGO,
            customerId: readingAttendance.value!.CODIGO_CLIENTE,
            contactId: readingAttendance.value!.CODIGO_NUMERO,
            resultId, scheduleDate
        });

        readingAttendance.reset();
    };

    function startAttendance(
        cliente: number,
        numero: number,
        wpp: string,
        pfp: string | null,
        template?: OficialWhatsappMessageTemplate,
        variables?: { cnpj: string, razao: string, nome: string }
    ) {
        if (user) {
            const data: any = {
                clientName: currentRoute,
                operatorId: user.CODIGO,
                customerId: cliente,
                contactId: numero,
                sectorId: user.SETOR,
                whatsappNumber: wpp
            };

            if (template && variables) {
                const currentSaudation = () => {
                    const currentTime = new Date();
                    const hour = currentTime.getHours();

                    if (hour >= 5 && hour < 12) {
                        return "Bom dia";
                    } else if (hour >= 12 && hour < 18) {
                        return "Boa tarde";
                    } else {
                        return "Boa noite";
                    }
                }

                const vars = {
                    "saudação_tempo": currentSaudation(),
                    "cliente_cnpj": formatarCPForCNPJ(variables!.cnpj),
                    "cliente_razao": variables.razao.trim(),
                    "contato_primeiro_nome": variables.nome.split(" ")[0],
                    "contato_nome_completo": variables.nome,
                    "atendente_nome": formatarNome(user.NOME),
                    "atendente_nome_exibição": formatarNome(user.NOME_EXIBICAO)
                }
                data["template"] = { ...template, variables: { ...vars } };
            };

            socket.value && socket.value.emit("start-attendance", data);
        };
    };

    function switchPauseAttendances() {
        if (socket.value && user) {
            pausedState.value ?
                socket.value.emit("resume-attendances", { clientName: currentRoute, operatorId: user.CODIGO })
                :
                socket.value.emit("pause-attendances", { clientName: currentRoute, operatorId: user.CODIGO })
        };
    };

    useEffect(() => {
        const audio = new Audio('./web_whatsapp.mp3');

        const handleNewAttendance = async (attendanceId: number) => {
            if (!detailedAttendances.value.some(a => a.CODIGO === attendanceId)) {
                const attendance = await api.get<SpecificAttendanceDetails>(`/custom-routes/specific-attendance/${attendanceId}`).then((res) => res.data);
                await loadMessages(attendance.CODIGO_NUMERO);
                detailedAttendances.set((prev) => [...prev, attendance]);

                new Notification('Novo atendimento', {
                    body: `[${attendance.RAZAO}]${attendance.CONTATO_NOME}`,
                    icon: logo
                });
            }
        };

        const handleStartedNewAttendance = async (attendanceId: number) => {
            if (!detailedAttendances.value.some(a => a.CODIGO === attendanceId)) {
                const attendance = await api.get<SpecificAttendanceDetails>(`/custom-routes/specific-attendance/${attendanceId}`).then((res) => res.data);
                await loadMessages(attendance.CODIGO_NUMERO);
                detailedAttendances.set(prev => [attendance, ...prev]);
                readingAttendance.set(attendance);

                new Notification('Novo atendimento', {
                    body: `[${attendance.RAZAO}] ${attendance.CONTATO_NOME}`,
                    icon: attendance.AVATAR_URL || logo
                });
            }
        }

        const handleRemoveAttendance = (id: number) => {
            detailedAttendances.set((prev) => prev.filter(a => a.CODIGO !== id));
            readingAttendance.set((prev) => {
                if (prev && prev.CODIGO === id) {
                    return null;
                }

                return prev;
            });
        };

        const handleUpdateAttendanceCustomer = (data: { attendanceId: number, CODIGO: number, RAZAO: string, CPF_CNPJ: string }) => {
            detailedAttendances.set((prev) => {
                const newArr = [...prev];
                const i = newArr.findIndex(a => a.CODIGO === data.attendanceId);
                if (~i) {
                    newArr[i] = { ...newArr[i], CODIGO_CLIENTE: data.CODIGO, RAZAO: data.RAZAO, CPF_CNPJ: data.CPF_CNPJ }
                }
                return newArr;
            });

            if (readingAttendance.value && readingAttendance.value.CODIGO === data.attendanceId) {
                readingAttendance.set(prev => ({ ...prev!, CODIGO_CLIENTE: data.CODIGO, RAZAO: data.RAZAO, CPF_CNPJ: data.CPF_CNPJ }))
            }
        };

        const handleNewMessage = async (attendanceMessage: AttendanceMessage) => {
            let messages: AttendanceMessage[] = [];
            const decodedMessage = decodeSafeURI(attendanceMessage.MENSAGEM);
            let attendance = detailedAttendances.value.find(a => a.CODIGO_NUMERO === attendanceMessage.CODIGO_NUMERO);

            if (user && user.NIVEL === 'ADMIN' && !attendance) {
                attendance = await api.get<SpecificAttendanceDetails>(`/custom-routes/specific-attendance/${attendanceMessage.CODIGO_ATENDIMENTO}`).then((res) => res.data);
                messages = await api.get<Array<AttendanceMessage>>(`/messages/${attendanceMessage.CODIGO_NUMERO}`).then((res) => res.data);
                attendanceMessage.STATUS = 'RECEIVED'
            }

            new Notification(`${attendanceMessage.CONTATO_NOME}`, {
                body: `${decodedMessage.length < 20 ? decodedMessage : decodedMessage.slice(0, 20) + "..."}`,
                icon: attendance?.AVATAR_URL || logo
            });

            attendanceMessage.DATA_HORA = new Date(attendanceMessage.DATA_HORA);

            if (audio) { audio.play(); }

            messages = attendancesMessagesGrouped.value[attendanceMessage.CODIGO_NUMERO] ? attendancesMessagesGrouped.value[attendanceMessage.CODIGO_NUMERO] : messages;

            if (messages) {
                messages.push(attendanceMessage);
                attendancesMessagesGrouped.set(prev => {
                    return { ...prev, [attendanceMessage.CODIGO_NUMERO]: messages }
                });

            }

        };
        const handleMessageStatus = async ({ LOCAL_ID, WAM_ID, STATUS, CODIGO_NUMERO }: MessageStatus) => {
            let messages = attendancesMessagesGrouped.value[+CODIGO_NUMERO] ? attendancesMessagesGrouped.value[+CODIGO_NUMERO] : [];
            if (user && user.NIVEL === 'ADMIN' && messages.length === 0) {
                messages = await api.get<Array<AttendanceMessage>>(`/messages/${CODIGO_NUMERO}`).then((res) => res.data);
            }
            const updatedMessages = messages.map((message) => {
                if (message.ID === WAM_ID || message.LOCAL_ID === LOCAL_ID) {
                    message.STATUS = STATUS;
                }
                return message
            })

            attendancesMessagesGrouped.set((prev) => {
                return { ...prev, [CODIGO_NUMERO]: updatedMessages }
            });
        };

        const handlePaused = () => {
            pausedState.set(true);
        };

        const handleResumed = () => {
            pausedState.set(false);
        };

        const handleUpdateUrgency = (data: { URGENCIA_OPERADOR?: OperatorUrgency, URGENCIA_SUPERVISOR?: SupervisorUrgency | null, CODIGO: number }) => {
            detailedAttendances.set(prev => prev.map(a => {
                const { CODIGO, ...updated } = data;

                if (CODIGO === a.CODIGO) {
                    return { ...a, ...updated };
                }

                return a;
            }));
        }

        if (socket.value) {
            socket.value.on("new-attendance", handleNewAttendance);
            socket.value.on("new-started-attendance", handleStartedNewAttendance);
            socket.value.on("remove-attendance", handleRemoveAttendance);
            socket.value.on("update-attendance-customer", handleUpdateAttendanceCustomer);
            socket.value.on("new-message", handleNewMessage);
            socket.value.on("message_status", handleMessageStatus);
            socket.value.on("paused", handlePaused);
            socket.value.on("resumed", handleResumed);
            socket.value.on("update-urgency", handleUpdateUrgency);
        }

        // Retorno de chamada de limpeza para desinscrição dos eventos
        return () => {
            if (socket.value) {
                socket.value.off("new-attendance", handleNewAttendance);
                socket.value.off("new-started-attendance", handleStartedNewAttendance);
                socket.value.off("remove-attendance", handleRemoveAttendance);
                socket.value.off("update-attendance-customer", handleUpdateAttendanceCustomer);
                socket.value.off("new-message", handleNewMessage);
                socket.value.off("message_status", handleMessageStatus);
                socket.value.off("paused", handlePaused);
                socket.value.off("resumed", handleResumed);
                socket.value.off("update-urgency", handleUpdateUrgency);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket.value, detailedAttendances.value]);

    return (
        <AttendancesContext.Provider value={{
            detailedAttendances,
            attendancesMessages,
            attendancesMessagesGrouped,
            readingAttendance,
            finishAttendance,
            modalState,
            startAttendance,
            pausedState,
            switchPauseAttendances,
            selectedTemplate,
            selectedClient,
            groups,
            readingGroup,
            newAttendanceModalState
        }}>
            {children}
            {
                newAttendanceModalState.value &&
                <Modal>
                    <NewAttendance />
                </Modal>

            }
        </AttendancesContext.Provider>
    );
};